import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  Loader2, 
  Clock, 
  MapPin,
  Phone,
  User,
  Calendar,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  ListChecks,
  FileText,
  Shield,
  Star,
  X,
  ArrowLeft,
  Mail,
  XCircle
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PublicClientSOW = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [sowData, setSowData] = useState(null);
  const [company, setCompany] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Enable scrolling for this page
  useEffect(() => {
    document.body.classList.add('public-proposal-page');
    return () => {
      document.body.classList.remove('public-proposal-page');
    };
  }, []);

  const fetchSOW = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/public/client-sow/${token}`);
      setSowData(res.data);
      setCompany(res.data.company);
    } catch (error) {
      console.error("Failed to load SOW", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSOW();
  }, [fetchSOW]);

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <Loader2 className="h-12 w-12 text-emerald-400 animate-spin" />
      </div>
    );
  }

  // Not found state
  if (!sowData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <Toaster position="top-center" />
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-slate-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Scope of Work Not Found</h1>
          <p className="text-slate-400">This link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  // Extract data
  const scopeSections = sowData?.scope_sections || [];
  const paymentMilestones = sowData?.payment_milestones || [];
  const projectTimeline = sowData?.project_timeline || [];
  const terms = sowData?.terms || [];
  const exclusions = sowData?.exclusions || [];
  const grandTotal = sowData?.grand_total || 0;
  const totalPercentage = paymentMilestones.reduce((sum, m) => sum + (m.percentage || m.percent || 0), 0);

  return (
    <div 
      className="min-h-screen py-8"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif",
        overflowY: "auto",
        overflowX: "hidden",
        height: "auto",
        minHeight: "100vh"
      }}
    >
      <Toaster position="top-center" />
      
      {/* Close/Back Button - Fixed at top */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => {
            if (window.history.length > 1) {
              window.history.back();
            } else {
              window.close();
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg border border-slate-600 backdrop-blur-sm transition-all shadow-lg"
          title="Back"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <button
          onClick={() => window.close()}
          className="flex items-center justify-center w-10 h-10 bg-slate-800/90 hover:bg-red-600 text-slate-300 hover:text-white rounded-lg border border-slate-600 backdrop-blur-sm transition-all shadow-lg"
          title="Close Tab"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Document Container */}
      <div 
        className="mx-auto"
        style={{ 
          maxWidth: "900px",
          backgroundColor: "#0f172a",
          borderRadius: "8px",
          border: "1px solid rgba(16, 185, 129, 0.3)",
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════════
            HEADER
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 pt-8 pb-6"
          style={{
            background: "linear-gradient(180deg, rgba(16, 185, 129, 0.15) 0%, transparent 100%)",
            borderBottom: "1px solid rgba(16, 185, 129, 0.3)",
            borderRadius: "8px 8px 0 0"
          }}
        >
          {/* Company Info */}
          <div className="mb-6">
            <h1 
              className="text-2xl font-bold tracking-wide"
              style={{ color: "#10b981", fontFamily: "'Playfair Display', serif" }}
            >
              {company?.company_name || sowData?.company_name || "Alpha Construction Pros"}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {company?.address || "Licensed & Insured Contractor"}
            </p>
            <p className="text-slate-500 text-xs mt-1">
              {company?.email || ""} {company?.phone ? `• ${company.phone}` : ""}
            </p>
          </div>
          
          {/* Document Metadata */}
          <div className="flex items-center gap-8 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">DOCUMENT ID</p>
              <p 
                className="text-white font-bold text-sm"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {sowData?.sow_id || "SOW-00001"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">DATE</p>
              <p className="text-white font-bold text-sm">{formatDate(sowData?.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">VALID FOR</p>
              <p className="text-white font-bold text-sm">{sowData?.valid_days || 30} Days</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">CONTRACT VALUE</p>
              <p className="text-emerald-400 font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {fmt(grandTotal)}
              </p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            TITLE & CLIENT INFO
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-5 w-5 text-emerald-400" />
            <p className="text-xs uppercase tracking-[4px] text-emerald-400">SCOPE OF WORK</p>
          </div>
          <h2 
            className="text-3xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif", lineHeight: 1.2 }}
          >
            {sowData?.project_name || "Project Scope of Work"}
          </h2>
          
          {/* Client Info Cards */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">PREPARED FOR</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-slate-500" />
                  <span className="text-white font-medium">{sowData?.client_name || "Client Name"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{sowData?.client_phone || "No phone on file"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-400">{sowData?.client_email || "No email on file"}</span>
                </div>
              </div>
            </div>
            
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">PROJECT LOCATION</p>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-slate-500 mt-0.5" />
                <span className="text-white font-medium">{sowData?.client_address || "Address TBD"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            PROJECT OVERVIEW
            ═══════════════════════════════════════════════════════════════════════════ */}
        {sowData?.project_overview && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">PROJECT OVERVIEW</p>
            </div>
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(30, 41, 59, 0.3)", borderLeft: "3px solid #10b981" }}
            >
              <p className="text-slate-300 leading-relaxed">{sowData.project_overview}</p>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            SCOPE SECTIONS
            ═══════════════════════════════════════════════════════════════════════════ */}
        {scopeSections.length > 0 && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">DETAILED SCOPE OF WORK</p>
            </div>
            
            <div className="space-y-4">
              {scopeSections.map((section, sectionIdx) => {
                const isExpanded = expandedSections[`scope-${sectionIdx}`] !== false;
                
                return (
                  <div 
                    key={section.id || sectionIdx}
                    className="rounded-lg overflow-hidden"
                    style={{ border: "1px solid rgba(16, 185, 129, 0.3)" }}
                  >
                    {/* Section Header */}
                    <button
                      onClick={() => toggleSection(`scope-${sectionIdx}`)}
                      className="w-full px-5 py-4 flex items-center justify-between"
                      style={{ backgroundColor: "rgba(16, 185, 129, 0.15)" }}
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                        <span className="text-white font-bold text-lg">{section.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-emerald-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          {fmt(section.total)}
                        </span>
                        {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="p-5" style={{ backgroundColor: "rgba(15, 23, 42, 0.5)" }}>
                        {/* Scope Description */}
                        {section.description && (
                          <p className="text-slate-300 mb-4">{section.description}</p>
                        )}
                        
                        {/* Items */}
                        {section.items && section.items.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Included Items:</p>
                            {section.items.map((item, itemIdx) => (
                              <div key={item.id || itemIdx} className="flex items-center gap-3 text-slate-300">
                                <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                <span>{item.name || item.itemName}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            PROJECT TIMELINE
            ═══════════════════════════════════════════════════════════════════════════ */}
        {projectTimeline.length > 0 && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-4 w-4 text-amber-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">PROJECT TIMELINE</p>
            </div>
            
            <div 
              className="p-5 rounded-lg"
              style={{ backgroundColor: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.3)" }}
            >
              <div className="space-y-3">
                {projectTimeline.map((phase, idx) => (
                  <div key={phase.id || idx} className="flex items-center gap-4">
                    <span 
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ backgroundColor: "rgba(245, 158, 11, 0.2)", color: "#fbbf24" }}
                    >
                      {idx + 1}
                    </span>
                    <span className="flex-1 text-white font-medium">{phase.phase}</span>
                    <span className="text-amber-400">{phase.duration}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            PAYMENT MILESTONES
            ═══════════════════════════════════════════════════════════════════════════ */}
        {paymentMilestones.length > 0 && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">PAYMENT SCHEDULE</p>
            </div>
            
            <div className="space-y-3">
              {paymentMilestones.map((milestone, idx) => (
                <div 
                  key={milestone.id || idx}
                  className="p-4 rounded-lg flex items-center justify-between"
                  style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.3)" }}
                >
                  <div className="flex-1">
                    <p className="text-white font-medium">{milestone.name}</p>
                    {milestone.description && (
                      <p className="text-slate-400 text-sm">{milestone.description}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {fmt(grandTotal * ((milestone.percentage || milestone.percent || 0) / 100))}
                    </p>
                    <p className="text-slate-500 text-sm">{milestone.percentage || milestone.percent || 0}%</p>
                  </div>
                </div>
              ))}
              
              {/* Total */}
              <div 
                className="p-4 rounded-lg flex items-center justify-between"
                style={{ backgroundColor: "rgba(16, 185, 129, 0.2)", border: "2px solid rgba(16, 185, 129, 0.5)" }}
              >
                <p className="text-white font-bold">Total Payment Schedule</p>
                <div className="text-right">
                  <p className="text-emerald-400 font-bold text-lg" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {fmt(grandTotal)}
                  </p>
                  <p className={`text-sm ${totalPercentage === 100 ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {totalPercentage}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            TERMS & CONDITIONS
            ═══════════════════════════════════════════════════════════════════════════ */}
        {terms.length > 0 && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-4 w-4 text-slate-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">TERMS & CONDITIONS</p>
            </div>
            
            <div className="space-y-2">
              {terms.map((term, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-400 text-sm">{term}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            EXCLUSIONS
            ═══════════════════════════════════════════════════════════════════════════ */}
        {exclusions.length > 0 && (
          <div className="px-10 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <XCircle className="h-4 w-4 text-red-400" />
              <p className="text-xs text-slate-500 uppercase tracking-widest">EXCLUSIONS</p>
            </div>
            
            <div className="space-y-2">
              {exclusions.map((exclusion, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <XCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-400 text-sm">{exclusion}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════════
            FOOTER
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 py-6 text-center"
          style={{ 
            borderTop: "1px solid rgba(100, 116, 139, 0.2)",
            backgroundColor: "rgba(15, 23, 42, 0.5)"
          }}
        >
          <p className="text-slate-500 text-sm">
            {company?.company_name || "Alpha Construction Pros"} • Licensed & Insured
          </p>
          <p className="text-slate-600 text-xs mt-1">
            This scope of work is valid for {sowData?.valid_days || 30} days from the date above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicClientSOW;
